import { TranslateService } from '@ngx-translate/core';
import {
  Validators,
  FormControl,
  FormGroup,
  FormArray,
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { Observable, of } from 'rxjs';
import { switchMap, map, delay } from 'rxjs/operators';
import { SelectOption } from '@app/shared/components/select/select.component';
import { Attribute, AttributeDataType } from '@dg/attributes-data-access';
import {
  BusinessRule,
  BusinessRuleFormGroup,
  BusinessRuleOutcome,
  BusinessRulePredicate,
  OutcomeControls,
  OutcomeFormGroup,
  PredicateControls,
  PredicateFormGroup,
  RuleTypes,
  StaticAttributes,
} from '../model';
import { isEmptyValidator } from '@app/shared/validators/is-empty.validator';
import { DropdownOption } from '../components/business-rule-upsert-modal/upsert-vm.factory';

type RuleNameValidator = (ruleName: string) => Observable<boolean>;

/**
 *
 * @param predicate
 * @returns Returns form group with controls with default values if a predicate is not provided.
 *          If a predicate is provided, form controls are created with values from the predicate.
 */
export function buildPredicateFormGroup(
  attributeOptions?: SelectOption[],
  predicate?: BusinessRulePredicate
): PredicateFormGroup {
  const selectedAttribute = attributeOptions?.find(
    // I don't love this, but as of right now, it's the only way to get the friendly display of the attribute title
    (x) => x.id === predicate?.fieldName
  );
  const controls: PredicateControls = {
    fieldName: new FormControl(predicate?.fieldName ?? '', [
      Validators.required,
    ]),
    fieldDisplayName: new FormControl(selectedAttribute?.title ?? ''),
    comparisonOperator: new FormControl(predicate?.comparisonOperator ?? '=', [
      Validators.required,
    ]),
    comparisonValue: new FormControl(predicate?.comparisonValue ?? '', [
      isEmptyValidator,
    ]),
  };

  return new FormGroup(controls);
}

/**
 *
 * @param outcome
 * @returns Returns form group with controls with default values if an outcome is not provided.
 *          If a outcome is provided, form controls are created with values from the outcome.
 */
export function buildOutcomeFormGroup(
  outcome?: BusinessRuleOutcome
): OutcomeFormGroup {
  const controls: OutcomeControls = {
    updateTypeResourceId: new FormControl(
      outcome?.updateTypeResourceId ?? null,
      [Validators.required]
    ),
    updateValue: new FormControl(outcome?.updateValue ?? ''),
    displayName: new FormControl(outcome?.displayName ?? ''),
  };

  if (outcome?.updateTypeResourceId !== 'DeleteUser') {
    controls.updateValue.addValidators(Validators.required);
  }

  return new FormGroup(controls);
}

/**
 *
 * @param rule
 * @returns Returns form group with controls with default values if a rule is not provided.
 *          If a rule is provided, form controls are created with values from the rule.
 */
export function createRuleUpsertForm(
  ruleType: RuleTypes,
  ruleNameValidator: RuleNameValidator,
  attributeOptions: SelectOption[],
  rule?: BusinessRule
): BusinessRuleFormGroup {
  const predicates = [];
  if (rule?.predicateOperators?.length > 0) {
    rule.predicateOperators.forEach((operator, index) => {
      if (index === 0) {
        // since predicates are chained together via outcomes, we only need to add the first predicate the first time around.
        // easy visualization of chaining: ab, bc, cd, de
        predicates.push(
          buildPredicateFormGroup(attributeOptions, operator.firstPredicate)
        );
      }
      predicates.push(
        buildPredicateFormGroup(attributeOptions, operator.secondPredicate)
      );
    });
  } else if (rule?.predicates.length === 1) {
    predicates.push(
      buildPredicateFormGroup(attributeOptions, rule.predicates[0])
    );
  } else {
    predicates.push(buildPredicateFormGroup(attributeOptions));
  }

  const formGroup = new FormGroup({
    businessRuleId: new FormControl(rule?.businessRuleId ?? undefined),
    ruleName: new FormControl(rule?.ruleName ?? '', {
      validators: [Validators.required],
      asyncValidators: [
        makeRuleNameValidator(ruleNameValidator, rule?.ruleName),
      ],
    }),
    predicates: new FormArray(predicates),
  });

  if (ruleType !== RuleTypes.Segment) {
    const outcomes = rule?.outcomes.map((outcome) =>
      buildOutcomeFormGroup(outcome)
    ) ?? [buildOutcomeFormGroup()];
    formGroup.addControl('outcomes', new FormArray(outcomes));
  }

  return formGroup;
}

export function getModalHeaderKey(
  currentRule: BusinessRule,
  ruleType: RuleTypes
): string {
  if (ruleType === RuleTypes.Segment) {
    return currentRule
      ? `BusinessRules_EditSegment`
      : `BusinessRules_CreateSegment`;
  }

  return currentRule ? `BusinessRules_EditRule` : `BusinessRules_AddRule`;
}

export function getRuleNameKey(ruleType: RuleTypes): string {
  return ruleType === RuleTypes.Segment
    ? 'BusinessRules_SegmentName'
    : 'BusinessRules_RuleName';
}

export function getRuleNamePlaceholder(ruleType: RuleTypes): string {
  return ruleType === RuleTypes.Segment
    ? 'BusinessRules_SegmentNamePlaceholder'
    : 'BusinessRules_RuleName';
}

export function getAddPredicateLabel(ruleType: RuleTypes): string {
  return ruleType === RuleTypes.Segment
    ? 'BusinessRules_AddAttribute'
    : 'BusinessRules_AddCondition';
}

export function makeAttributeOptions(
  attributes: Attribute[],
  translate: TranslateService,
  existingRule?: BusinessRule
): SelectOption[] {
  const apiAttributes = attributes.map((x) => x.attributeName);

  if (!existingRule) {
    return apiAttributes.map((x) => makeOption(x, translate));
  }

  // allow pre existing attributes in the dropdown if they don't exist in the response
  const existingAttributes = existingRule?.predicates.map(
    (predicate) => predicate.fieldName
  );

  const hiddenAttributes = existingAttributes.filter(
    (x) => !apiAttributes.includes(x)
  );

  const combinedOptions = [...new Set([...apiAttributes, ...hiddenAttributes])] // use Set to remove duplicates
    .sort((a, b) => a.localeCompare(b))
    .map((x) => makeOption(x, translate));

  return combinedOptions;
}

export function makeOutcomeOptions(
  outcomeActions: string[],
  translate: TranslateService,
  existingRule?: BusinessRule
): DropdownOption[] {
  if (!existingRule) {
    return outcomeActions.map((x) => makeOutcomeOption(x, translate));
  }

  // allow pre existing outcomes in the dropdown if they don't exist in the response
  const existingOutcomes = existingRule?.outcomes.map(
    (outcome) => outcome.updateTypeResourceId
  );

  const hiddenOutcomes = existingOutcomes.filter(
    (x) => !outcomeActions.includes(x)
  );

  const combinedOptions = [...new Set([...outcomeActions, ...hiddenOutcomes])] // use Set to remove duplicates
    .sort((a, b) => a.localeCompare(b))
    .map((x) => makeOutcomeOption(x, translate));

  return combinedOptions;
}

export function attributeNameToType(
  loadedAttributes: Attribute[],
  selectedAttributeName: string
) {
  return (
    (loadedAttributes
      .find((x) => x.attributeName === selectedAttributeName)
      ?.attributeDataType?.toLowerCase() as AttributeDataType) ??
    AttributeDataType.String
  );
}

function makeRuleNameValidator(
  validatorFn: RuleNameValidator,
  existingRuleName: string
): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors> => {
    const buildResponse = (exists: boolean) =>
      exists ? { ruleNameUnavailable: true } : null;
    const checkIfExists = (ruleName: string) => {
      const isSame = ruleName === existingRuleName;
      return isSame ? of(false) : validatorFn(ruleName);
    };
    // Trim the value to remove any extra whitespace in name that may break the string search
    return of(control.value?.trim()).pipe(
      delay(250), // delay to allow rapid typing + async validation to cancel in flight checks
      switchMap(checkIfExists),
      map(buildResponse)
    );
  };
}

function makeOption(x: string, translate: TranslateService) {
  return {
    id: x,
    title:
      x === StaticAttributes.APPLY_TO_ALL_USERS ||
      x === StaticAttributes.APPLY_TO_NEW_USERS
        ? translate.instant(`BusinessRules_${x}`)
        : x,
    ...((x === StaticAttributes.APPLY_TO_ALL_USERS ||
      x === StaticAttributes.APPLY_TO_NEW_USERS) && {
      groupingId: 'applyUsers',
    }),
  };
}

function makeOutcomeOption(
  x: string,
  translate: TranslateService
): DropdownOption {
  return {
    name: translate.instant(`BusinessRules_${x}`),
    value: x,
  };
}
